import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useToggle} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {CalendarsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {
  defaultCalendarEventMaxParticipants,
  findNumberOfParticipantsToDisplay,
} from '../../../../srcCommon/helpers/calendarEventHelpers';
import {ParticipantTypesEnum} from '../../../models/api/participantModel';
import {
  ArbitraryCalendarEventParticipantModel,
  VisualParticipantModel,
} from '../../../models/api/visualParticipantModel';
import {CalendarEventParticipantsItem} from './calendarEventParticipantsItem';

/*
 * Props.
 */

interface CalendarEventParticipantsListProps {
  panelId: CalendarsPanelId;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  onVisualParticipantsChange?: (arg: ReadonlyArray<VisualParticipantModel>) => void;
  shouldShowAllParticipants?: boolean;
  readonlyContactUids?: ReadonlyArray<string>;
  shouldDisableTypeButton?: boolean;
  isInEventEditor?: boolean;
}

/*
 * Styles.
 */

export const StyledParticipantsDiv = styled.div`
  display: grid;
  margin-left: -8px;
  grid-row-gap: 2px;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 4px;
  margin-left: -12px;
`;

/*
 * Component.
 */

export const CalendarEventParticipantsList: FC<CalendarEventParticipantsListProps> = (props) => {
  const [areParticipantsExpanded, toggleParticipantsExpanded] = useToggle(false);

  const attendees = props.visualParticipants.filter((p) => p.isAttendee);
  const numParticipantsToDisplay = findNumberOfParticipantsToDisplay(
    attendees,
    props.shouldShowAllParticipants,
    areParticipantsExpanded,
  );

  return (
    <>
      <StyledParticipantsDiv>
        {_(attendees)
          .take(numParticipantsToDisplay)
          .map((p) => renderParticipantsItem(props, p))
          .value()}
      </StyledParticipantsDiv>
      {maybeRenderMoreButton(
        props.shouldShowAllParticipants,
        props.visualParticipants,
        areParticipantsExpanded,
        toggleParticipantsExpanded,
        numParticipantsToDisplay,
      )}
    </>
  );
};

function renderParticipantsItem(
  props: CalendarEventParticipantsListProps,
  visualParticipant: VisualParticipantModel,
) {
  const {readonlyContactUids, visualParticipants, panelId, isInEventEditor, onVisualParticipantsChange} =
    props;

  const isReadonlyParticipant =
    readonlyContactUids && readonlyContactUids.includes(visualParticipant.contact.uid);
  if (!onVisualParticipantsChange || isReadonlyParticipant) {
    // Render a read-only participant item.
    return (
      <CalendarEventParticipantsItem
        panelId={panelId}
        key={visualParticipant.contact.uid}
        visualParticipant={visualParticipant}
        isInEventEditor={isInEventEditor}
      />
    );
  }

  const onRemove = () => {
    if (!visualParticipant.isCreator && !visualParticipant.isOrganizer) {
      onVisualParticipantsChange(
        visualParticipants.filter((p) => p.contact.uid !== visualParticipant.contact.uid),
      );
      return;
    }

    // Since this participant is the creator or organizer, just mark them as not attendees instead of completely removing.
    const newParticipants = visualParticipants.map((p) => {
      if (p.contact.uid !== visualParticipant.contact.uid) {
        return p;
      }

      return {
        ...p,
        isAttendee: false,
      };
    });
    onVisualParticipantsChange(newParticipants);
  };

  const onTypeChange = (type: ParticipantTypesEnum) => {
    const newParticipants = visualParticipants.map((p) => {
      if (p.contact.uid === visualParticipant.contact.uid) {
        return {
          ...p,
          type,
        };
      }

      return p;
    });

    onVisualParticipantsChange(newParticipants);
  };

  return (
    <CalendarEventParticipantsItem
      panelId={panelId}
      key={visualParticipant.contact.uid}
      visualParticipant={visualParticipant}
      onRemove={onRemove}
      onTypeChange={onTypeChange}
      shouldDisableTypeButton={props.shouldDisableTypeButton}
      isInEventEditor={isInEventEditor}
    />
  );
}

export function maybeRenderMoreButton(
  shouldShowAllParticipants: boolean | undefined,
  visualParticipants:
    | ReadonlyArray<VisualParticipantModel>
    | ReadonlyArray<ArbitraryCalendarEventParticipantModel>,
  areParticipantsExpanded: boolean,
  toggleParticipantsExpanded: () => void,
  numParticipantsToDisplay: number,
) {
  if (shouldShowAllParticipants) {
    return null;
  }

  if (visualParticipants.length < defaultCalendarEventMaxParticipants) {
    return null;
  }

  return (
    <StyledButtonWrapperDiv>
      <Button variant={ButtonVariantsEnum.LINK} onClick={toggleParticipantsExpanded}>
        {renderMoreButtonContent(
          areParticipantsExpanded,
          visualParticipants.length - numParticipantsToDisplay,
        )}
      </Button>
    </StyledButtonWrapperDiv>
  );
}

function renderMoreButtonContent(areParticipantsExpanded: boolean, count: number) {
  if (areParticipantsExpanded) {
    return <Trans id="calendar-participants-list-seeless">See less</Trans>;
  }

  return (
    <>
      <Trans id="calendar-participants-list-seemore">See more</Trans>
      {` (${count})`}
    </>
  );
}
